import React, { useEffect, useState, useCallback } from "react";
import { api, fmtPrice, apiError } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { statusLabel, statusColor } from "@/lib/status";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MapPin, Truck, Package, Loader2 } from "lucide-react";
import { ShareLocationButton } from "@/components/ShareLocationButton";
import { toast } from "sonner";

export default function DeliveryDashboard() {
  const { t } = useLang();
  const [available, setAvailable] = useState([]);
  const [mine, setMine] = useState([]);

  const load = useCallback(async () => {
    const [a, m] = await Promise.all([api.get("/delivery/available"), api.get("/delivery/mine")]);
    setAvailable(a.data); setMine(m.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const accept = async (id) => {
    try { await api.post(`/delivery/${id}/accept`); toast.success(t("accept")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setStatus = async (id, status) => {
    try { await api.put(`/orders/${id}/status`, { status }); toast.success(statusLabel(t, status)); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const OrderRow = ({ o, actions }) => (
    <div className="bg-white border border-stone-200 rounded-xl p-5" data-testid={`delivery-order-${o.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div><p className="font-display font-bold">{o.order_no}</p><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusColor(o.status)}`}>{statusLabel(t, o.status)}</span></div>
        <span className="font-display font-extrabold text-emerald-800">{fmtPrice(o.total)}</span>
      </div>
      <div className="text-sm text-stone-600 flex items-start gap-2">
        <MapPin className="w-4 h-4 text-mango-600 mt-0.5 shrink-0" />
        <div>
          <p className="font-medium text-stone-800">{o.address?.full_name} · {o.address?.phone}</p>
          <p>{o.address?.street} {o.address?.district}, {o.address?.city}</p>
        </div>
      </div>
      <p className="text-xs text-stone-400 mt-2">{o.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")}</p>
      <div className="mt-3 flex gap-2">{actions}</div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display font-extrabold text-3xl tracking-tight mb-8 flex items-center gap-2"><Truck className="w-7 h-7 text-emerald-800" /> {t("delivery_dashboard")}</h1>
      <Tabs defaultValue="available">
        <TabsList>
          <TabsTrigger value="available" data-testid="tab-available">{t("available_deliveries")} ({available.length})</TabsTrigger>
          <TabsTrigger value="mine" data-testid="tab-mine">{t("my_deliveries")} ({mine.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="available" className="mt-5 space-y-3">
          {available.map((o) => (
            <OrderRow key={o.id} o={o} actions={<Button size="sm" className="bg-mango-600 hover:bg-mango-700 rounded-full" onClick={() => accept(o.id)} data-testid={`accept-${o.id}`}>{t("accept")}</Button>} />
          ))}
          {available.length === 0 && <p className="text-stone-400 py-16 text-center"><Package className="w-10 h-10 mx-auto mb-2 text-stone-300" />{t("no_deliveries")}</p>}
        </TabsContent>

        <TabsContent value="mine" className="mt-5 space-y-3">
          {mine.map((o) => (
            <OrderRow key={o.id} o={o} actions={
              <>
                <ShareLocationButton orderId={o.id} />
                {o.status !== "out_for_delivery" && o.status !== "delivered" && <Button size="sm" variant="outline" onClick={() => setStatus(o.id, "out_for_delivery")} data-testid={`out-${o.id}`}>{t("mark_out")}</Button>}
                {o.status !== "delivered" && <Button size="sm" className="bg-emerald-800 hover:bg-emerald-900" onClick={() => setStatus(o.id, "delivered")} data-testid={`delivered-${o.id}`}>{t("mark_delivered")}</Button>}
              </>
            } />
          ))}
          {mine.length === 0 && <p className="text-stone-400 py-16 text-center">{t("no_deliveries")}</p>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
